import axios from 'axios';
import { Timesheet, UserRelationshipType } from '@timesheet/schemas';
import { getApiBaseUrl } from '../lib/api-base-url';

// Extend Timesheet for cases where id might be optional (during creation)
type TimesheetInput = Omit<Timesheet, 'id' | 'createdAt' | 'updatedAt'> & { id?: number };

export type TimesheetState = 'draft' | 'in_approval' | 'approved' | 'rejected';

export interface TimesheetEntry extends Timesheet {
  state: TimesheetState;
  rejectionReason?: string;
  submittedAt?: string;
  reviewedAt?: string;
  reviewedBy?: number;
  order?: {
    id: number;
    code: string;
    description?: string;
  };
}

/** Mirrors be/src/timesheets/helpers/timesheet-issues.ts */
export type TimesheetIssueCode =
  | 'LEAVE_CONFLICT'
  | 'DAILY_LIMIT_EXCEEDED'
  | 'UNASSIGNED_ORDER'
  | 'WEEKEND_HOURS'
  | 'HOLIDAY_HOURS'
  | 'COMPANY_CLOSING_HOURS'
  | 'MISSING_DAYS'
  | 'SHORT_DAYS'
  | 'OVERTIME_ON_SHORT_DAY'
  | 'MISSING_ORDER';

export type TimesheetIssueSeverity = 'error' | 'warning';

export interface TimesheetIssue {
  code: TimesheetIssueCode;
  severity: TimesheetIssueSeverity;
  /** yyyy-MM-dd, ascending */
  dates: string[];
  hours?: number;
  /** MISSING_DAYS only: missing days that a pending leave request would cover */
  pendingLeaveDays?: number;
}

export interface PendingApproval {
  user: { id: number; name: string; relationshipType?: UserRelationshipType };
  entries: TimesheetEntry[];
  totalHours: number;
  stats: {
    workedDays: number;
    vacationDays: number;
    onCallDays: number;
    permissionDays: number;
    sicknessDays: number;
    rolDays: number;
  };
  /** Automatic consistency checks for the month, errors first; only the management endpoint fills it */
  issues?: TimesheetIssue[];
}

/** Working days and expected hours of a month, net of national holidays and company closings (management endpoint). */
export interface MonthCalendar {
  standardDailyHours: number;
  /** Mon–Fri days that are neither a holiday nor a company closing */
  workingDays: number;
  /** workingDays × standardDailyHours */
  expectedHours: number;
  /** yyyy-MM-dd, weekdays only, ascending */
  holidayDates: string[];
  closingDates: string[];
}

export interface PendingCount {
  count: number;
  userCount: number;
}

const API_URL = getApiBaseUrl();

export interface ManagementFilters {
  userId?: number;
  orderId?: number;
  userCompanyId?: number;
  clientCompanyId?: number;
  /** Only people whose direct approver is the logged-in user */
  onlyMine?: boolean;
  /** Also list the people with nothing submitted this month */
  includeWithoutEntries?: boolean;
  /** PDF only: the desk's name search, which the list applies in the browser */
  search?: string;
}

const managementParams = (year: number, month: number, filters?: ManagementFilters) => {
  const { onlyMine, includeWithoutEntries, ...rest } = filters ?? {};
  return {
    year,
    month,
    ...rest,
    ...(onlyMine ? { onlyMine: 1 } : {}),
    ...(includeWithoutEntries ? { includeWithoutEntries: 1 } : {}),
  };
};

export const timesheetService = {
  getAll: async (startDate: string, endDate: string): Promise<Timesheet[]> => {
    const response = await axios.get(`${API_URL}/timesheets`, {
      params: { startDate, endDate },
    });
    return response.data;
  },

  getMonthlyEntries: async (year: number, month: number): Promise<TimesheetEntry[]> => {
    const response = await axios.get(`${API_URL}/timesheets/monthly`, {
      params: { year, month },
    });
    return response.data;
  },

  getDashboardStats: async (startDate: string, endDate: string) => {
    const response = await axios.get(`${API_URL}/timesheets/dashboard-stats`, {
      params: { startDate, endDate },
    });
    return response.data;
  },

  save: async (data: TimesheetInput): Promise<Timesheet> => {
    const response = await axios.post(`${API_URL}/timesheets`, data);
    return response.data;
  },

  batchSave: async (data: TimesheetInput[]): Promise<Timesheet[]> => {
    const response = await axios.post(`${API_URL}/timesheets/batch`, data);
    return response.data;
  },

  // Approval workflow methods
  submitForApproval: async (id: number): Promise<TimesheetEntry> => {
    const response = await axios.post(`${API_URL}/timesheets/${id}/submit`);
    return response.data;
  },

  bulkSubmitForApproval: async (ids: number[]): Promise<BulkOperationResult> => {
    const response = await axios.post(`${API_URL}/timesheets/bulk-submit`, { ids });
    return response.data;
  },

  getPendingApprovals: async (filters?: { userId?: number; orderId?: number }): Promise<PendingApproval[]> => {
    const response = await axios.get(`${API_URL}/timesheets/pending`, { params: filters });
    return response.data;
  },

  getManagementTimesheets: async (
    year: number,
    month: number,
    filters?: ManagementFilters,
  ): Promise<PendingApproval[]> => {
    const response = await axios.get(`${API_URL}/timesheets/management`, {
      params: managementParams(year, month, filters),
    });
    return response.data;
  },

  getManagementMonthCalendar: async (year: number, month: number): Promise<MonthCalendar> => {
    const response = await axios.get(`${API_URL}/timesheets/management/month-calendar`, {
      params: { year, month },
    });
    return response.data;
  },

  downloadManagementPdf: async (
    year: number,
    month: number,
    filters?: ManagementFilters,
  ): Promise<Blob> => {
    const response = await axios.get(`${API_URL}/timesheets/management/download-pdf`, {
      params: managementParams(year, month, filters),
      responseType: 'blob',
    });
    return response.data;
  },

  getPendingCount: async (): Promise<PendingCount> => {
    const response = await axios.get(`${API_URL}/timesheets/pending-count`);
    return response.data;
  },

  fillAbsences: async (
    year: number,
    month: number,
  ): Promise<Array<{
    user: { id: number; name: string };
    updated: Array<{ date: string; reason: string; hours: number }>;
  }>> => {
    const response = await axios.post(`${API_URL}/timesheets/management/fill-absences`, {
      year,
      month,
    });
    return response.data;
  },

  removeCompanyClosingAbsences: async (
    startDate: string,
    endDate: string,
  ): Promise<Array<{
    user: { id: number; name: string };
    updated: Array<{ date: string; reason: string; hours: number }>;
  }>> => {
    const response = await axios.post(`${API_URL}/timesheets/management/remove-company-closing-absences`, {
      startDate,
      endDate,
    });
    return response.data;
  },

  previewCompanyClosing: async (
    startDate: string,
    endDate: string,
  ): Promise<{
    validDays: string[];
    updated: Array<{
      user: { id: number; name: string };
      updated: Array<{ date: string; reason: string; hours: number }>;
    }>;
  }> => {
    const response = await axios.post(`${API_URL}/timesheets/management/preview-company-closing`, {
      startDate,
      endDate,
    });
    return response.data;
  },

  updateState: async (
    ids: number[],
    state: 'approved' | 'rejected',
    rejectionReason?: string
  ): Promise<BulkOperationResult> => {
    const response = await axios.patch(`${API_URL}/timesheets/bulk-state`, {
      ids,
      state,
      rejectionReason,
    });
    return response.data;
  },

  updateStateWithExpenses: async (
    ids: number[],
    state: 'approved' | 'rejected',
    approveClaims: boolean = false,
    rejectionReason?: string
  ): Promise<BulkOperationResult> => {
    const response = await axios.patch(`${API_URL}/timesheets/bulk-state-with-expenses`, {
      ids,
      state,
      approveClaims,
      rejectionReason,
    });
    return response.data;
  },
};

export interface BulkOperationResult {
  succeeded: TimesheetEntry[];
  failed: { id: number; reason: string }[];
}
